import Swiper from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import './styles/main.scss';

const TRACKING_STORE_URLS = {
  appstore:
    'https://6325045.redirect.appmetrica.yandex.com/?appmetrica_tracking_id=1038857724282073317&referrer=reattribution%3D1',
  googleplay:
    'https://6325045.redirect.appmetrica.yandex.com?appmetrica_tracking_id=966800127928883339&referrer=reattribution%3D1',
} as const;

const DEFAULT_TRACKING_STORE_URLS = {
  appstore:
    'https://6325045.redirect.appmetrica.yandex.com/?utm_source=landing&utm_campaign=release-kmp&appmetrica_tracking_id=1038857724282073317&referrer=reattribution%3D1',
  googleplay:
    'https://6325045.redirect.appmetrica.yandex.com?appmetrica_tracking_id=966800127928883339&referrer=reattribution%3D1&utm_source=landing&utm_campaign=release-kmp',
} as const;

const DIRECT_STORE_URLS = {
  appstore: 'https://apps.apple.com/ru/app/x5-run/id6759965433',
  googleplay: 'https://play.google.com/store/apps/details?id=com.x5run',
} as const;

function init() {
  initStoreLinks();
  initMobileNav();
  initScrollReveal();
  initSmoothAnchorNav();
  initAboutSwiper();
}

init();

function isStoreTrackingEnabled() {
  return (
    document.querySelector('meta[name="store-tracking"]')?.getAttribute('content') === 'on'
  );
}

function initStoreLinks() {
  const trackingEnabled = isStoreTrackingEnabled();

  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  const utmCampaign = params.get('utm_campaign');

  const extra = new URLSearchParams();
  if (utmSource) extra.set('utm_source', utmSource);
  if (utmCampaign) extra.set('utm_campaign', utmCampaign);
  const extraQuery = extra.toString();

  document.querySelectorAll<HTMLAnchorElement>('[data-store-link]').forEach((link) => {
    const store = link.dataset.storeLink;
    if (store !== 'appstore' && store !== 'googleplay') return;

    if (!trackingEnabled) {
      link.href = DIRECT_STORE_URLS[store];
      return;
    }

    link.href = extraQuery
      ? `${TRACKING_STORE_URLS[store]}&${extraQuery}`
      : DEFAULT_TRACKING_STORE_URLS[store];
  });
}

function initScrollReveal() {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const elements = document.querySelectorAll<HTMLElement>('[data-animate-on-scroll]');

  if (reducedMotion) {
    elements.forEach((el) => el.classList.add('in-view'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
        }
      });
    },
    {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0,
    }
  );

  elements.forEach((el) => observer.observe(el));
}

function initSmoothAnchorNav() {
  document.addEventListener('click', (e) => {
    const link = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[href]');
    if (!link) return;
    const href = link.getAttribute('href') ?? '';
    const hashMatch = href.match(/^\/?#([\w-]+)$/);
    if (!hashMatch) return;
    const id = hashMatch[1];
    const target = document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth' });
  });
}

function initMobileNav() {
  const burger = document.querySelector<HTMLButtonElement>('.header__burger');
  const mobileNav = document.querySelector<HTMLElement>('.header__mobile-nav');
  const closeBtn = document.querySelector<HTMLButtonElement>('.header__mobile-nav-close');
  const overlay = document.querySelector<HTMLElement>('.header__mobile-nav-overlay');

  if (!burger || !mobileNav) return;

  const openMenu = () => {
    overlay?.setAttribute('aria-hidden', 'false');
    mobileNav.classList.add('open');
    mobileNav.setAttribute('aria-hidden', 'false');
    burger.setAttribute('aria-label', 'Закрыть меню');
    burger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('mobile-nav-open');
  };

  const closeMenu = () => {
    overlay?.setAttribute('aria-hidden', 'true');
    mobileNav.classList.remove('open');
    mobileNav.setAttribute('aria-hidden', 'true');
    burger.setAttribute('aria-label', 'Открыть меню');
    burger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('mobile-nav-open');
  };

  burger.addEventListener('click', () => {
    if (mobileNav.classList.contains('open')) closeMenu();
    else openMenu();
  });

  closeBtn?.addEventListener('click', closeMenu);
  overlay?.addEventListener('click', closeMenu);

  mobileNav.querySelectorAll<HTMLElement>('.nav__link').forEach((link) => {
    link.addEventListener('click', closeMenu);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && mobileNav.classList.contains('open')) closeMenu();
  });

  closeMenu();
  requestAnimationFrame(() => {
    mobileNav.classList.add('is-ready');
  });
}

function initAboutSwiper() {
  const container = document.querySelector('.about-swiper');
  if (!container) return;

  const swiper = new Swiper('.about-swiper', {
    loop: false,
    slidesPerView: 'auto',
    spaceBetween: 24,
    centeredSlides: true,
    centeredSlidesBounds: true,
    slideToClickedSlide: true,
    allowTouchMove: true,
    simulateTouch: true,
    speed: 400,
    on: {
      init(swiperInstance) {
        swiperInstance.slideTo(0, 0);
        setActiveSlideClassByIndex(swiperInstance, swiperInstance.realIndex);
        updateNavigationState(swiperInstance);
      },
      slideChange(swiperInstance) {
        setActiveSlideClassByIndex(swiperInstance, swiperInstance.realIndex);
        updateNavigationState(swiperInstance);
      },
      slideChangeTransitionEnd(swiperInstance) {
        setActiveSlideClassByIndex(swiperInstance, swiperInstance.realIndex);
        updateNavigationState(swiperInstance);
      },
      tap(swiperInstance, event) {
        const target = event.target as HTMLElement;
        const clickedSlide = target.closest('.swiper-slide');
        if (clickedSlide) {
          const index = swiperInstance.slides.indexOf(clickedSlide as HTMLElement);
          if (index !== -1) setActiveSlideClassByIndex(swiperInstance, index);
        }
      },
    },
  });

  const prevBtn = container.querySelector<HTMLButtonElement>('.about-swiper__btn--prev');
  const nextBtn = container.querySelector<HTMLButtonElement>('.about-swiper__btn--next');
  const lastIndex = swiper.slides.length - 1;

  prevBtn?.addEventListener('click', () => {
    if (swiper.realIndex > 0) swiper.slidePrev();
  });
  nextBtn?.addEventListener('click', () => {
    if (swiper.realIndex < lastIndex) swiper.slideNext();
  });
}

function setActiveSlideClassByIndex(swiper: Swiper, activeIndex: number) {
  swiper.slides.forEach((slide, index) => {
    slide.classList.toggle('about-swiper__slide--active', index === activeIndex);
  });
}

function updateNavigationState(swiper: Swiper) {
  const container = swiper.el;
  const lastIndex = swiper.slides.length - 1;
  const isFirst = swiper.realIndex === 0;
  const isLast = swiper.realIndex === lastIndex;
  const nextEl = container.querySelector<HTMLButtonElement>('.about-swiper__btn--next');
  const prevEl = container.querySelector<HTMLButtonElement>('.about-swiper__btn--prev');

  if (nextEl) {
    nextEl.classList.toggle('swiper-button-disabled', isLast);
    nextEl.setAttribute('aria-disabled', String(isLast));
    nextEl.disabled = isLast;
  }
  if (prevEl) {
    prevEl.classList.toggle('swiper-button-disabled', isFirst);
    prevEl.setAttribute('aria-disabled', String(isFirst));
    prevEl.disabled = isFirst;
  }
}
